import { useMemo, useState } from 'react';
import { Button, Badge, Row, Col, Card, CardBody } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { HasPermission } from '../../rbac/HasPermission';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  useApiCatalogList, useApiCatalogUsage, useDeleteCatalog,
} from './useApiCatalog';
import { ApiCatalogModal } from './ApiCatalogModal';

const fmtEur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const fmtNum = new Intl.NumberFormat('de-DE');

/**
 * #151 API-Marktplatz-Admin (brand-scoped).
 * Katalog-Einträge + Preis-Tiers editieren; Requests + wiederkehrender Umsatz
 * je API. Quelle: GET /v1/admin/api-catalog (+ /usage).
 */
export function ApiCatalogPage() {
  const [editing, setEditing] = useState(null);   // catalog row | 'new'
  const [pendingDelete, setPendingDelete] = useState(null);

  const listQ = useApiCatalogList();
  const usageQ = useApiCatalogUsage(30);
  const del = useDeleteCatalog();

  const catalog = listQ.data?.catalog ?? [];
  const usageBySlug = useMemo(() => {
    const m = {};
    for (const a of usageQ.data?.apis ?? []) m[a.slug] = a;
    return m;
  }, [usageQ.data]);

  const totals = useMemo(() => {
    let requests = 0, subs = 0, mrr = 0;
    for (const a of usageQ.data?.apis ?? []) {
      requests += a.requests || 0; subs += a.activeSubscriptions || 0; mrr += a.mrrCents || 0;
    }
    return { requests, subs, mrr };
  }, [usageQ.data]);

  return (
    <div>
      <PageHeader
        title="API-Marktplatz"
        subtitle="Katalog, Tarife & Umsatz der öffentlichen APIs · GET /v1/admin/api-catalog"
        actions={
          <HasPermission perms={['products.write']}>
            <Button color="primary" onClick={() => setEditing('new')}>+ Neue API</Button>
          </HasPermission>
        }
      />

      {/* Kennzahlen (letzte 30 Tage) */}
      <Row className="mb-3">
        <Col md="4">
          <Card className="mb-2"><CardBody>
            <div className="text-muted small text-uppercase">Requests (30 T.)</div>
            <div className="h4 mb-0">{fmtNum.format(totals.requests)}</div>
          </CardBody></Card>
        </Col>
        <Col md="4">
          <Card className="mb-2"><CardBody>
            <div className="text-muted small text-uppercase">Aktive Abos</div>
            <div className="h4 mb-0">{fmtNum.format(totals.subs)}</div>
          </CardBody></Card>
        </Col>
        <Col md="4">
          <Card className="mb-2"><CardBody>
            <div className="text-muted small text-uppercase">MRR (API-Abos)</div>
            <div className="h4 mb-0">{fmtEur.format(totals.mrr / 100)}</div>
          </CardBody></Card>
        </Col>
      </Row>

      <DataTable
        loading={listQ.isLoading}
        error={listQ.error}
        empty={!catalog.length}
        emptyMessage="Noch keine APIs im Katalog."
        onRetry={() => listQ.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 130 }}>Slug</th>
            <th>Name</th>
            <th style={{ width: 120 }}>Kategorie</th>
            <th style={{ width: 80 }} className="text-end">Tarife</th>
            <th style={{ width: 110 }} className="text-end">Requests 30T</th>
            <th style={{ width: 110 }} className="text-end">MRR</th>
            <th style={{ width: 90 }}>Sichtbar</th>
            <th style={{ width: 170 }} />
          </tr>
        </thead>
        <tbody>
          {catalog.map((c) => {
            const u = usageBySlug[c.slug] || {};
            return (
              <tr key={c.id}>
                <td><code className="small">{c.slug}</code></td>
                <td>
                  <div className="fw-semibold">{c.name}</div>
                  {c.tagline && <div className="small text-muted" style={{ maxWidth: 340 }}>{c.tagline}</div>}
                </td>
                <td>{c.category || <span className="text-muted">—</span>}</td>
                <td className="text-end">{fmtNum.format(c.tierCount || 0)}</td>
                <td className="text-end">{fmtNum.format(u.requests || 0)}</td>
                <td className="text-end">{fmtEur.format((u.mrrCents || 0) / 100)}</td>
                <td>
                  {c.isPublic
                    ? <Badge color="success" pill>öffentlich</Badge>
                    : <Badge color="secondary" pill>versteckt</Badge>}
                </td>
                <td className="text-end">
                  <HasPermission perms={['products.read']}>
                    <Button color="primary" outline size="sm" className="me-2" onClick={() => setEditing(c)}>
                      Bearbeiten
                    </Button>
                  </HasPermission>
                  <HasPermission perms={['products.write']}>
                    <Button color="danger" outline size="sm" onClick={() => setPendingDelete(c)}>
                      Löschen
                    </Button>
                  </HasPermission>
                </td>
              </tr>
            );
          })}
        </tbody>
      </DataTable>

      {editing && (
        <ApiCatalogModal
          key={editing === 'new' ? 'new' : `edit-${editing.id}`}
          isOpen
          catalogId={editing === 'new' ? null : editing.id}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="API aus dem Katalog löschen?"
        body={pendingDelete ? (
          <>
            <p className="mb-2">
              <strong>{pendingDelete.name}</strong> (<code>{pendingDelete.slug}</code>) und ihre
              Endpoint-Doku werden entfernt.
            </p>
            <p className="small text-muted mb-0">
              Die Preis-Produkte (Tarife) bleiben bestehen — bestehende Abos laufen weiter.
              Nur der öffentliche Marktplatz-Eintrag verschwindet.
            </p>
          </>
        ) : null}
        confirmLabel="Löschen"
        confirmColor="danger"
        busy={del.isPending}
        onConfirm={async () => { await del.mutateAsync({ id: pendingDelete.id }); setPendingDelete(null); }}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
